import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, type ChangeEvent } from "react";
import { Search, ExternalLink, Filter, Target, ChevronDown, ChevronUp } from "lucide-react";
import {
  DashboardShell,
  PageHeading,
  fmt,
  fmtRca,
  LoadingPanel,
} from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import { countBy } from "@/lib/analytics";
import {
  leadDistance,
  leadOrigin,
  leadOtherRca,
  leadRca,
  leadScore,
  leadSupplierFit,
  perspectiveConfig,
  rcaTone,
  supplierFitTone,
} from "@/lib/perspective";
import type { Lead } from "@/lib/trade-types";

export const Route = createFileRoute("/leads")({
  head: () => ({ meta: [{ title: "Buyer Leads — Ready4Trade" }] }),
  component: LeadsPage,
});

function LeadsPage() {
  const { leads, aggregates, isLoading, perspective } = useTradeData();
  const config = perspectiveConfig(perspective);
  const [query, setQuery] = useState("");
  const [hs2, setHs2] = useState("");
  const [country, setCountry] = useState("");
  const [sector, setSector] = useState("");
  const [rcaFilter, setRcaFilter] = useState<"all" | "above" | "below" | "missing">("all");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [maxKm, setMaxKm] = useState(20000);
  const [sortMode, setSortMode] = useState<"score" | "distance" | "newest" | "rca">("score");
  const [expanded, setExpanded] = useState<number | null>(null);

  const sectors = useMemo(() => (leads ? countBy(leads.map((lead) => lead.sec)) : []), [leads]);
  const destinations = useMemo(() => (leads ? countBy(leads.map((lead) => lead.dc)) : []), [leads]);
  const chapters = useMemo(
    () =>
      leads
        ? countBy(leads.filter((lead) => lead.h2).map((lead) => lead.h2)).map((row) => ({
            ...row,
            label: aggregates?.agg.hs2_names[row.name] || "",
          }))
        : [],
    [aggregates, leads],
  );

  const filtered = useMemo(() => {
    if (!leads) return [];
    const needle = query.trim().toLowerCase();
    const output = leads.filter((lead) => {
      const rca = leadRca(lead, perspective);
      const distance = leadDistance(lead, perspective);
      const fit = leadSupplierFit(lead, perspective);
      if (hs2 && lead.h2 !== hs2) return false;
      if (country && lead.dc !== country) return false;
      if (sector && lead.sec !== sector) return false;
      if (supplierFilter && fit !== supplierFilter) return false;
      if (rcaFilter === "above" && !((rca ?? 0) > 1)) return false;
      if (rcaFilter === "below" && !(rca != null && rca <= 1)) return false;
      if (rcaFilter === "missing" && rca != null) return false;
      if (leadScore(lead, perspective) < minScore) return false;
      if (distance != null && distance > maxKm) return false;
      if (needle) {
        const haystack =
          `${lead.t} ${lead.p} ${lead.h6} ${lead.hd} ${lead.bo} ${lead.sp} ${lead.d} ${lead.dc} ${lead.bf} ${lead.sf}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });

    if (sortMode === "distance")
      output.sort(
        (a, b) =>
          (leadDistance(a, perspective) ?? Number.MAX_SAFE_INTEGER) -
          (leadDistance(b, perspective) ?? Number.MAX_SAFE_INTEGER),
      );
    if (sortMode === "score")
      output.sort(
        (a, b) =>
          leadScore(b, perspective) - leadScore(a, perspective) ||
          (b.dt ?? "").localeCompare(a.dt ?? ""),
      );
    if (sortMode === "newest")
      output.sort(
        (a, b) =>
          (b.dt ?? "").localeCompare(a.dt ?? "") ||
          leadScore(b, perspective) - leadScore(a, perspective),
      );
    if (sortMode === "rca")
      output.sort(
        (a, b) =>
          (leadRca(b, perspective) ?? -1) - (leadRca(a, perspective) ?? -1) ||
          leadScore(b, perspective) - leadScore(a, perspective),
      );
    return output;
  }, [
    country,
    hs2,
    leads,
    maxKm,
    minScore,
    perspective,
    query,
    rcaFilter,
    sector,
    sortMode,
    supplierFilter,
  ]);

  return (
    <DashboardShell meta={aggregates?.meta}>
      <PageHeading
        title={`${config.flag} Buyer leads ledger`}
        subtitle={`Search and rank leads from ${config.country}'s perspective using HS 2022, pooled RCA, supplier scope and nearest-origin distance.`}
        badge={leads ? `${fmt(filtered.length)} of ${fmt(leads.length)} leads` : undefined}
      />

      {isLoading && <LoadingPanel />}

      {aggregates && leads && (
        <section className="surface-panel p-4 md:p-6">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
            <Filter className="w-3.5 h-3.5" /> Filters update the ledger immediately
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-8 gap-3 mb-5">
            <div className="md:col-span-2 xl:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                placeholder="Search product, HS, specification, buyer or destination…"
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-secondary border border-transparent text-sm focus:outline-none focus:border-primary/40 focus:bg-card"
              />
            </div>
            <select
              value={sector}
              onChange={(event) => setSector(event.target.value)}
              className="filter-select"
            >
              <option value="">All sectors</option>
              {sectors.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.name} ({item.count})
                </option>
              ))}
            </select>
            <select
              value={hs2}
              onChange={(event) => setHs2(event.target.value)}
              className="filter-select"
            >
              <option value="">All HS chapters</option>
              {chapters.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.name} · {item.label} ({item.count})
                </option>
              ))}
            </select>
            <select
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              className="filter-select"
            >
              <option value="">All destinations</option>
              {destinations.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.name} ({item.count})
                </option>
              ))}
            </select>
            <select
              value={rcaFilter}
              onChange={(event) => setRcaFilter(event.target.value as typeof rcaFilter)}
              className="filter-select"
            >
              <option value="all">All RCA statuses</option>
              <option value="above">RCA above 1</option>
              <option value="below">RCA 1 or below</option>
              <option value="missing">RCA unavailable</option>
            </select>
            <select
              value={supplierFilter}
              onChange={(event) => setSupplierFilter(event.target.value)}
              className="filter-select"
            >
              <option value="">All supplier scopes</option>
              <option value="Explicit">Explicit</option>
              <option value="Worldwide">Worldwide</option>
              <option value="Regional">Regional</option>
              <option value="Unclear">Unclear</option>
              <option value="Not listed">Not listed</option>
            </select>
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value as typeof sortMode)}
              className="filter-select"
            >
              <option value="score">Sort: opportunity score</option>
              <option value="rca">Sort: highest RCA</option>
              <option value="distance">Sort: nearest first</option>
              <option value="newest">Sort: newest first</option>
            </select>

            <div className="md:col-span-1 xl:col-span-4 flex items-center gap-3 px-1">
              <label className="text-xs text-muted-foreground whitespace-nowrap">
                Minimum score {minScore}
              </label>
              <input
                type="range"
                min={0}
                max={90}
                step={10}
                value={minScore}
                onChange={(event) => setMinScore(Number(event.target.value))}
                className="flex-1 accent-[color:var(--color-primary)]"
              />
            </div>
            <div className="md:col-span-1 xl:col-span-4 flex items-center gap-3 px-1">
              <label className="text-xs text-muted-foreground whitespace-nowrap">
                Maximum {fmt(maxKm)} km
              </label>
              <input
                type="range"
                min={500}
                max={20000}
                step={500}
                value={maxKm}
                onChange={(event) => setMaxKm(Number(event.target.value))}
                className="flex-1 accent-[color:var(--color-primary)]"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm min-w-[1280px]">
              <thead className="bg-secondary text-muted-foreground">
                <tr className="text-left">
                  <Th>Score</Th>
                  <Th>Product / HS2022</Th>
                  <Th className="text-right">Pooled RCA</Th>
                  <Th>Market</Th>
                  <Th className="text-right">Distance</Th>
                  <Th>Supplier fit</Th>
                  <Th>Terms</Th>
                  <Th>Date</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 300).map((lead) => (
                  <LeadRows
                    key={lead.i}
                    lead={lead}
                    perspective={perspective}
                    open={expanded === lead.i}
                    onToggle={() => setExpanded(expanded === lead.i ? null : lead.i)}
                  />
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={9} className="px-3 py-12 text-center text-muted-foreground">
                      No leads match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > 300 && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Showing the first 300 of {fmt(filtered.length)} matching leads. Narrow the filters for
              a shorter target list.
            </p>
          )}
        </section>
      )}
    </DashboardShell>
  );
}

function LeadRows({
  lead,
  perspective,
  open,
  onToggle,
}: {
  lead: Lead;
  perspective: "UZB" | "SVK";
  open: boolean;
  onToggle: () => void;
}) {
  const score = leadScore(lead, perspective);
  const rca = leadRca(lead, perspective);
  const otherRca = leadOtherRca(lead, perspective);
  const distance = leadDistance(lead, perspective);
  const origin = leadOrigin(lead, perspective);
  const fit = leadSupplierFit(lead, perspective);
  const selectedCountry = perspectiveConfig(perspective).country;
  const otherCountry = perspective === "UZB" ? "Slovakia" : "Uzbekistan";
  return (
    <>
      <tr className="border-t border-border hover:bg-secondary/50 align-top">
        <td className="px-3 py-3">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${score >= 70 ? "bg-emerald-50 text-emerald-700" : score >= 50 ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}
          >
            <Target className="w-3 h-3" /> {score}
          </span>
        </td>
        <td className="px-3 py-3 max-w-[340px]">
          <div className="font-medium text-foreground leading-snug">{lead.p || lead.t}</div>
          <div className="text-[11px] text-muted-foreground truncate">{lead.t}</div>
          {lead.h6 ? (
            <div className="mt-1">
              <span className="code-chip text-primary">{lead.h6}</span>
              <span className="text-[10px] text-muted-foreground ml-2">{trim(lead.hd, 60)}</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">HS unavailable</span>
          )}
        </td>
        <td className="px-3 py-3 text-right whitespace-nowrap">
          <span
            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${rcaTone(rca)}`}
          >
            {fmtRca(rca)}
          </span>
        </td>
        <td className="px-3 py-3 max-w-[190px]">
          <div className="font-medium truncate">{lead.dc}</div>
          <div className="text-[10px] text-muted-foreground truncate">{lead.d}</div>
          <div className="text-[10px] text-muted-foreground">Buyer from {lead.bf}</div>
        </td>
        <td className="px-3 py-3 text-right whitespace-nowrap font-mono text-xs">
          {distance != null ? (
            <>
              {fmt(distance)} km
              <div className="text-[10px] text-muted-foreground">from {origin || "origin"}</div>
            </>
          ) : (
            "—"
          )}
        </td>
        <td className="px-3 py-3 whitespace-nowrap">
          <span
            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${supplierFitTone(fit)}`}
          >
            {fit}
          </span>
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-xs">
          <div>{lead.pay || "—"}</div>
          <div className="text-muted-foreground">{lead.sh || "—"}</div>
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-xs text-muted-foreground">
          {lead.dt || "—"}
        </td>
        <td className="px-3 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <button
              onClick={onToggle}
              aria-label="Show lead details"
              className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground"
            >
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {lead.u && (
              <a
                href={lead.u}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open source lead"
                className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/15"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-slate-50/70">
          <td colSpan={9} className="px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4 text-xs">
              <Detail label="Specifications" value={lead.sp || "Not supplied"} />
              <Detail label="Looking for suppliers from" value={lead.sf || "Not supplied"} />
              <Detail
                label="Quantity / target price"
                value={`${lead.q || "Not supplied"} · ${lead.tp || "price not supplied"}`}
              />
              <Detail
                label="HS standardization"
                value={`${lead.hsStatus}${lead.provisional ? " · provisional" : ""}`}
              />
              <Detail label={`${selectedCountry} pooled RCA`} value={fmtRca(rca)} />
              <Detail label={`${otherCountry} pooled RCA`} value={fmtRca(otherRca)} />
              <Detail
                label="Annual RCA (2022 / 2023 / 2024)"
                value={
                  perspective === "UZB"
                    ? `${fmtRca(lead.ru22)} / ${fmtRca(lead.ru23)} / ${fmtRca(lead.ru24)}`
                    : `${fmtRca(lead.rs22)} / ${fmtRca(lead.rs23)} / ${fmtRca(lead.rs24)}`
                }
              />
              <Detail
                label="Opportunity score note"
                value="Score combines pooled RCA, supplier-scope fit, nearest-origin distance, recency, payment/shipping information and HS status."
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
        {label}
      </div>
      <div className="text-foreground leading-relaxed">{value}</div>
    </div>
  );
}
function Th({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-3 text-[10px] font-semibold uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}
function trim(value: string, length: number) {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value;
}
